import {
  Button,
  ButtonGroup,
  type ButtonProps,
} from '@progress/kendo-react-buttons'
import classNames from 'classnames'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useToggle } from 'react-use'

import { HStack } from '~/components/elements/HStack'
import { Typography } from '~/components/elements/Typography'
import { useLogEvent } from '~/hooks/useLogEvent'
import { type Group, groups, toGroupIndex } from '~/utils'

import styles from './GroupingPanelInner.css'

export type GroupingPanelInnerProps = {
  'data-testid'?: string
  disabled?: boolean
  group: Group
  onChange?: (group: Group) => void
}

export const GroupingPanelInner = ({
  'data-testid': dataTestId,
  disabled,
  group,
  onChange,
}: GroupingPanelInnerProps) => {
  const { t } = useTranslation()
  const logEvent = useLogEvent()

  const [collapsed, toggleCollapsed] = useToggle(true)

  const handleGroupingButtonClick = React.useCallback<
    (group: Group) => ButtonProps['onClick']
  >(
    (group) => () => {
      onChange?.(group)

      logEvent(`Define group ${toGroupIndex(group)}`)
    },
    [logEvent, onChange],
  )

  return (
    <HStack data-testid={dataTestId} spacing={1}>
      <ButtonGroup className={styles.buttonGroup} disabled={disabled}>
        {groups.slice(0, collapsed ? 5 : 15).map((group_) => (
          <Button
            key={group_}
            className={classNames(styles.button, styles[`button-${group_}`])}
            selected={group_ === group}
            togglable={true}
            onClick={handleGroupingButtonClick(group_)}
          >
            <Typography variant="heading2">
              <span className={styles.buttonText}>
                {t('group_2')}

                {toGroupIndex(group_)}
              </span>
            </Typography>
          </Button>
        ))}
      </ButtonGroup>

      <Button
        className={styles.collapseButton}
        fillMode="flat"
        icon={collapsed ? 'arrow-chevron-right' : 'arrow-chevron-down'}
        onClick={toggleCollapsed}
      />
    </HStack>
  )
}
